<template>
	<div>
		<h2 class="content-block">Imports</h2>
		<div class="content-block manage-imports">
				<DxDataGrid :data-source="imports" key-expr="guid" :show-borders="true" height="auto" @row-prepared="onRowPrepared" @row-expanding="onRowExpanding">
					<DxToolbar>
						<DxItem location="before" name="advancedModeToggle" widget="dxButton" :options="advancedModeButtonOptions" />
					</DxToolbar>
				<DxSearchPanel :visible="true" width="300" />
				<DxPaging :enabled="true" />
				<DxColumn data-field="guid" caption="GUID" width="360" />
				<DxColumn data-field="primary_count" caption="Primary Rows" data-type="number" alignment="right" width="140" />
				<DxColumn data-field="extended_count" caption="Extended Rows" data-type="number" alignment="right" width="140" />
				<DxColumn data-field="created_at" caption="Created" data-type="datetime" :sort-index="0" sort-order="desc" width="220" />
				<DxColumn data-field="group_id" caption="Group ID" data-type="number" width="120" />
				<DxColumn data-field="type_id" caption="Type" width="200" cell-template="typeCell" />
				<DxColumn caption="Actions" cell-template="actionsCell" width="220" alignment="center" />
				<DxMasterDetail :enabled="true" template="detailTemplate" />
				<template #actionsCell="{ data }">
					<div style="display:flex; gap:8px; justify-content:center;">
						<DxBtn icon="check" hint="Accept import" :type="acceptBtnType(data.data)" :styling-mode="acceptBtnMode(data.data)" :disabled="isDecisionSet(data.data) || actionBusy" @click="onAcceptClick(data.data)" />
						<DxBtn icon="undo" hint="Rollback import" :type="rollbackBtnType(data.data)" :styling-mode="rollbackBtnMode(data.data)" :disabled="isDecisionSet(data.data) || actionBusy" @click="onRollbackClick(data.data)" />
					</div>
				</template>
				<template #typeCell="{ data }">
					<span>{{ formatTypeId(data.data) }}</span>
				</template>
				<template #detailTemplate="{ data }">
					<div style="padding:8px 0;">
						<div v-if="detailLoading[data.data.guid]" style="padding:8px 12px; color:#666;">Loading...</div>
						<div v-else>
							<DxDataGrid :data-source="detailRowsByGuid[data.data.guid] || []" :show-borders="true" height="auto">
								<DxColumn data-field="reference_id" caption="Mote" data-type="number" width="100" alignment="right" />
								<DxColumn data-field="row_id" caption="Row" data-type="number" width="80" alignment="right" />
								<DxColumn data-field="subrow_id" caption="Subrow" data-type="number" width="100" alignment="right" />
								<DxColumn data-field="ref" caption="Item" />
							</DxDataGrid>
							<div v-if="advancedMode" class="detail-footer">
								<div>
									<DxBtn v-if="data.data.decision === true" icon="deletecolumn" type="danger" text="Force delete motes" :disabled="detailLoading[data.data.guid] || actionBusy" @click="onForceDeleteMotesClick(data.data.guid)" />
								</div>
								<div class="detail-footer-right">
									<DxBtn icon="floppy" text="Dump to Console" :disabled="detailLoading[data.data.guid] || actionBusy" @click="onLogDetailsClick(data.data.guid)" />
									<DxBtn icon="clear" type="danger" text="Remove import log entry" :disabled="detailLoading[data.data.guid] || actionBusy" @click="onRemoveImportLogClick(data.data.guid)" />
								</div>
							</div>
						</div>
					</div>
				</template>
			</DxDataGrid>
			<DxLoadPanel :visible="actionBusy" :show-indicator="true" :shading="true" shading-color="rgba(0,0,0,0.25)" :hide-on-outside-click="false" message="Processing..." />
		</div>
	</div>
</template>

<style lang="scss">
.manage-imports {
	.dx-datagrid .dx-row.dx-data-row.dx-row-import-accepted > td {
		background-color: rgba(0, 128, 0, 0.06);
	}
	.dx-datagrid .dx-row.dx-data-row.dx-row-import-rolledback > td {
		background-color: rgba(255, 0, 0, 0.06);
	}
	.dx-button.dx-button-danger.dx-state-disabled.dx-button-mode-contained {
		background-color: #f443367d;
		.dx-icon {
			color: #00000066;
		}
	}
	.dx-button.dx-button-success.dx-state-disabled.dx-button-mode-contained {
		background-color: #8bc34a7d;
		.dx-icon {
			color: #00000066;
		}
	}
	.detail-footer {
		margin-top: 8px;
		padding-top: 8px;
		border-top: 1px dashed #ccc;
		display: flex;
		gap: 8px;
		justify-content: space-between;
		align-items: center;
	}
	.detail-footer-right {
		display: flex;
		gap: 8px;
	}
}
</style>

<script setup>
import { ref, onMounted, inject, computed } from 'vue';
import DxDataGrid, { DxColumn, DxSearchPanel, DxPaging, DxMasterDetail, DxToolbar, DxItem } from 'devextreme-vue/data-grid';
import DxBtn from 'devextreme-vue/button';
import DxLoadPanel from 'devextreme-vue/load-panel';
import { alert as dxAlert, confirm as dxConfirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { SchemaCache } from '../schemas';

const $http = inject('$http');
const imports = ref([]);
const advancedMode = ref(false);
const isLoading = ref(false);
const loadError = ref(null);
const actionBusy = ref(false);
const detailRowsByGuid = ref({});
const detailLoading = ref({});

const parseDate = (value) => {
	if (!value) return null;
	// Accepts "YYYY-MM-DD HH:mm:ss" or ISO; returns Date or null
	try {
		// Replace space with 'T' if needed to improve parsing consistency
		const normalized = typeof value === 'string' && value.indexOf('T') === -1 ? value.replace(' ', 'T') : value;
		const d = new Date(normalized);
		return isNaN(d.getTime()) ? null : d;
	} catch (e) {
		return null;
	}
};

const mapImportRow = (item) => {
	const md = item && typeof item.metadata === 'object' && item.metadata !== null ? item.metadata : {};
	return {
		guid: item?.guid ?? '',
		primary_count: typeof item?.primary_count === 'number' ? item.primary_count : (item?.primary_count ? Number(item.primary_count) : 0),
		extended_count: typeof item?.extended_count === 'number' ? item.extended_count : (item?.extended_count ? Number(item.extended_count) : 0),
		created_at: parseDate(md?.date) ?? null,
		group_id: Number.isFinite(md?.group_id) ? md.group_id : (md?.group_id ? Number(md.group_id) : null),
		type_id: Number.isFinite(md?.type_id) ? md.type_id : (md?.type_id ? Number(md.type_id) : null),
		decision: typeof md?.decision === 'boolean' ? md.decision : null,
	};
};

const loadImports = async () => {
	isLoading.value = true;
	loadError.value = null;
	try {
		const response = await $http.get('/importlog');
		const data = Array.isArray(response?.data) ? response.data : [];
		imports.value = data.map(mapImportRow);
	} catch (err) {
		console.error('Failed to load imports', err);
		loadError.value = err?.message || 'Failed to load imports';
		imports.value = [];
	} finally {
		isLoading.value = false;
	}
};

const ACCEPT_CONFIRM_MESSAGE = "Accepting this import will mark these items as \"real\" and allow users to interact with them normally.<br>After this, you may no longer easily delete these items, as users may have changed them or added relationships to them.";
const ROLLBACK_CONFIRM_MESSAGE = "Rolling back this import will remove these imported items and delete them permanently.<br>You may then re-import them as normal. Because these items were hidden from users, they should be safe to remove this way.";
const FORCE_DELETE_MOTES_CONFIRM_MESSAGE = "Are you sure you want to force deletion of motes referenced in this log entry?<br>This may have adverse effects if users have interacted with these items.<br>No safety checking or reference checking is done whether this will be safe!";

const formatTypeId = (row) => {
	const id = row?.type_id;
	if (id === null || id === undefined) return '';
	const schema = SchemaCache.getSchemaById(id);
	return schema?.type ? schema.type : `#${id}`;
};

const acceptImport = async (row) => {
	actionBusy.value = true;
	try {
		await $http.post(`/importlog/${row.guid}/accept`);
		await loadImports();
	} catch (error) {
		const message = (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ? (error.response.data.message || error.response.data.error) : (error && error.message ? error.message : 'Failed to accept import');
		dxAlert(message, 'Accept Import Failed');
	} finally {
		actionBusy.value = false;
	}
};

const rollbackImport = async (row) => {
	actionBusy.value = true;
	try {
		await $http.post(`/importlog/${row.guid}/rollback`);
		await loadImports();
	} catch (error) {
		const message = (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ? (error.response.data.message || error.response.data.error) : (error && error.message ? error.message : 'Failed to rollback import');
		dxAlert(message, 'Rollback Import Failed');
	} finally {
		actionBusy.value = false;
	}
};

const isDecisionSet = (row) => typeof row?.decision === 'boolean';
const acceptBtnType = (row) => row?.decision === true ? 'success' : (row?.decision === false ? 'normal' : 'normal');
const rollbackBtnType = (row) => row?.decision === false ? 'danger' : (row?.decision === true ? 'normal' : 'normal');
const acceptBtnMode = (row) => row?.decision === true ? 'contained' : (row?.decision === false ? 'text' : 'outlined');
const rollbackBtnMode = (row) => row?.decision === false ? 'contained' : (row?.decision === true ? 'text' : 'outlined');

const onAcceptClick = async (row) => {
    if (isDecisionSet(row) || actionBusy.value) return;
    const result = await dxConfirm(ACCEPT_CONFIRM_MESSAGE, 'Confirm Accept Import');
    if (result) {
        acceptImport(row);
    }
};
const onRollbackClick = async (row) => {
    if (isDecisionSet(row) || actionBusy.value) return;
    const result = await dxConfirm(ROLLBACK_CONFIRM_MESSAGE, 'Confirm Rollback Import');
    if (result) {
        rollbackImport(row);
    }
};

const onRowPrepared = (e) => {
	if (e.rowType !== 'data' || !e.data) return;
	if (e.data.decision === true) {
		e.rowElement.classList.add('dx-row-import-accepted');
	} else if (e.data.decision === false) {
		e.rowElement.classList.add('dx-row-import-rolledback');
	}
};

const mapDetailRow = (item) => {
	const rowId = Number.isFinite(item?.row_id) ? item.row_id : (item?.row_id ? Number(item.row_id) : null);
	const subrowId = Number.isFinite(item?.subrow_id) ? item.subrow_id : (item?.subrow_id ? Number(item.subrow_id) : null);
	const referenceId = Number.isFinite(item?.reference_id) ? item.reference_id : (item?.reference_id ? Number(item.reference_id) : null);
	let refText = '';
	const isRelation = !!item?.is_relation;
	const entityObj = item?.reference;
	if (entityObj == null) {
		refText = isRelation ? `Deleted Relation #${referenceId}` : `Deleted Mote #${referenceId}`;
	} else {
		const payload = entityObj?.payload !== undefined ? entityObj.payload : entityObj;
		try {
			refText = JSON.stringify(payload);
		} catch (e) {
			refText = String(payload);
		}
	}
	return {
		row_id: rowId,
		subrow_id: subrowId,
		ref: refText,
		reference_id: referenceId,
	};
};

const loadImportDetails = async (guid) => {
	if (!guid) return;
	if (detailRowsByGuid.value[guid] || detailLoading.value[guid]) return;
	detailLoading.value[guid] = true;
	try {
		const resp = await $http.get(`/importlog/${guid}`);
		const items = Array.isArray(resp?.data) ? resp.data : [];
		const mapped = items.map(mapDetailRow);
		detailRowsByGuid.value[guid] = mapped;
	} catch (err) {
		console.error('Failed to load import details', guid, err);
		detailRowsByGuid.value[guid] = [];
	} finally {
		detailLoading.value[guid] = false;
	}
};

const onRowExpanding = (e) => {
	const guid = e?.key || (e?.data && e.data.guid) || (e?.data?.data && e.data.data.guid);
	loadImportDetails(guid);
};

const onLogDetailsClick = (guid) => {
	const rows = detailRowsByGuid.value[guid] || [];
	console.log('Import detail rows for', guid, rows);
};
const onRemoveImportLogClick = async (guid) => {
	// Only allow if we have made a decision and it is true or false, otherwise it is too dangerous to remove.
	const row = imports.value.find(row => row.guid === guid);
	if (!row || !isDecisionSet(row)) {
		dxAlert('You must make a decision on this import before you can remove it.', 'Remove Import Log Entry Failed');
		return;
	}
	const result = await dxConfirm('Are you sure you want to remove this import log entry? This action cannot be undone.', 'Confirm Remove Import Log Entry');
	if (result) {
		try {
			await $http.delete(`/importlog/${guid}`);
			notify({ message: 'Import log entry removed.', type: 'success', displayTime: 2000, position: 'top right' });
			await loadImports();
		} catch (error) {
			const backendMessage = (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ? (error.response.data.message || error.response.data.error) : (error && error.message ? error.message : 'Failed to remove import log entry');
			notify({ message: backendMessage, type: 'error', displayTime: 3000, position: 'top right' });
		}
	}
};
const onForceDeleteMotesClick = async (guid) => {
	// Only allow if we have made a decision and it is true.
	const row = imports.value.find(row => row.guid === guid);
	if (!row || row.decision !== true) {
		dxAlert('You must have accepted this import (decision is true) before you can force delete motes.', 'Force Delete Motes Failed');
		return;
	}
	const result = await dxConfirm(FORCE_DELETE_MOTES_CONFIRM_MESSAGE, 'Confirm Force Delete Motes');
	if (result) {
		try {
			await $http.post(`/importlog/${guid}/purge`);
			notify({ message: 'Motes force deleted.', type: 'success', displayTime: 2000, position: 'top right' });
			await loadImports();
		} catch (error) {
			const backendMessage = (error && error.response && error.response.data && (error.response.data.message || error.response.data.error)) ? (error.response.data.message || error.response.data.error) : (error && error.message ? error.message : 'Failed to force delete motes');
			notify({ message: backendMessage, type: 'error', displayTime: 3000, position: 'top right' });
		}
	}
};

const advancedModeButtonOptions = computed(() => ({
	text: advancedMode.value ? 'Advanced mode: ON' : 'Advanced mode',
	type: advancedMode.value ? 'danger' : '',
	hint: 'Toggle advanced debug controls',
	stylingMode: advancedMode.value ? 'outlined' : 'text',
	icon: 'preferences',
	onClick: () => { advancedMode.value = !advancedMode.value; }
}));

onMounted(async () => {
	try {
		if (SchemaCache && typeof SchemaCache.waitForData === 'function')
			await SchemaCache.waitForData();
	} finally {
		loadImports();
	}
});

</script>
